// @ts-strict-ignore
import reduxWebsocket from '@giantmachines/redux-websocket';
import { configureStore } from '@reduxjs/toolkit';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { matchPath, Route, Router, Switch } from 'react-router-dom';
import createSagaMiddleware from 'redux-saga';
import rootReducer from './reducers/reducers';
import { rootSaga } from './sagas/sagas';
import '../css/index.less';
import * as amplitude from '@amplitude/analytics-browser';
import { pageUrlEnrichmentPlugin } from '@amplitude/plugin-page-url-enrichment-browser';
import { StyleProvider } from '@ant-design/cssinjs';
import { LoadingOutlined } from '@ant-design/icons';
import * as Sentry from '@sentry/react';
import { Integrations as TracingIntegrations } from '@sentry/tracing';
import ConfigProvider from 'antd/lib/config-provider';
import Spin from 'antd/lib/spin';
import antdTheme from 'antd/lib/theme';
import { createBrowserHistory } from 'history';
import * as LogRocket from 'logrocket';
import * as Logger from '@/utils/logger';
import MainAppContainer from './components/MainAppContainer';
import { PublicAppContainer } from './components/public/PublicAppContainer';
import { antGrey, getAntdThemeTokens, runwayColors } from './components/styles/globalStyles';
import { ThemeModeProvider, useThemeMode } from './components/styles/themeMode';
import { AMPLITUDE_API_KEY_DEV, AMPLITUDE_API_KEY_PROD, GA_MEASUREMENT_ID } from './constants';
import { isDevelopmentEnvironment } from './helpers';
import { allRoutes } from './helpers/routeHelpers';
import { defaultApi } from './middlewares/defaultApi';
import { paginationApi } from './middlewares/paginationApi';
import { FETCH_ORG_DESTINATIONS_AND_VERSIONS_SUCCESS } from './actions/orgDestinationsAndVersions';
import { FETCH_ORG_SUMMARY_VIEW_SUCCESS } from './actions/orgSummaryView';
import routes from './routes';
import {
    REDUX_WEBSOCKET_CLOSED,
    REDUX_WEBSOCKET_CONNECT,
    REDUX_WEBSOCKET_ERROR,
    REDUX_WEBSOCKET_MESSAGE,
    REDUX_WEBSOCKET_OPEN,
} from './sagas/wsSagas';
import type { Environment } from './types';
import { config } from './utils/ga';
import * as HubspotUtils from './utils/hubspot';
import { initializeAmplitudeExperimentClient } from './utils/internalFeatureFlags';

const environment = process.env.NODE_ENV as Environment;
const isDevEnvironment = isDevelopmentEnvironment();

const history = createBrowserHistory();

const appRoutes = allRoutes(routes);

// LogRocket init is deferred until after org scripts config is loaded.
// See initializeClientScripts() in scriptManager.ts.

// Sentry

Sentry.init({
    environment: environment,
    dsn: 'https://7effbdf3a8e14892bf3e33d34ca6e553@o461692.ingest.sentry.io/5463811',
    integrations: [
        new TracingIntegrations.BrowserTracing({
            routingInstrumentation: Sentry.reactRouterV5Instrumentation(history, appRoutes, matchPath),
        }),
    ],
    beforeSend(event) {
        // the original error, if needed.
        // const error = hint?.originalException;

        // Filter by message - Claydar (session recording) causes play/pause race conditions
        const errorMessage = event.exception?.values?.[0]?.value || '';
        if (errorMessage.includes('The play() request was interrupted')) {
            Logger.log(`Filtered out Sentry event based on message: ${JSON.stringify(event)}`);
            return null;
        }

        // Filter by stack trace filename
        const stack = event.exception?.values?.[0]?.stacktrace?.frames || [];
        const isLinkedInInsights = stack.some((frame) => frame.filename?.includes('li.lms-analytics'));
        const isReactDom = stack.some((frame) => frame.filename?.includes('react-dom.production'));
        const isHubspotAnalytics = stack.some((frame) => frame.filename?.includes('js.hs-analytics.net'));
        // Mouseflow uses /projects/{uuid}.js URLs
        const isMouseflow = stack.some((frame) => frame.filename?.match(/\/projects\/[a-f0-9-]+\.js/));
        const isLogRocket = stack.some((frame) => frame.filename?.includes('LogRocket.min.js'));

        if (isLinkedInInsights || isReactDom || isHubspotAnalytics || isMouseflow || isLogRocket) {
            Logger.log(`Filtered out possible Sentry event based on file name: ${JSON.stringify(event)}`);
            return null;
        }

        return event;
    },
    release: SENTRY_RELEASE,
    tracesSampleRate: 0.2,
});

// Amplitude
const apiKey = isDevEnvironment ? AMPLITUDE_API_KEY_DEV : AMPLITUDE_API_KEY_PROD;
const pageUrlEnrichment = pageUrlEnrichmentPlugin();
amplitude.add(pageUrlEnrichment);
amplitude.init(apiKey, {
    appVersion: PACKAGE_VERSION,
    defaultTracking: {
        attribution: true,
        pageViews: false,
        sessions: false,
        formInteractions: false,
        fileDownloads: false,
    },
    // logLevel: amplitude.Types.LogLevel.Debug, //uncomment for debug amplitude logging
});

// Amplitude Experiments / Feature flags - note, do not move, must be initialized AFTER the main Amplitude analytics SDK

initializeAmplitudeExperimentClient(isDevEnvironment);

// Middlewares

const sagaMiddleware = createSagaMiddleware();
const reduxWebsocketMiddleware = reduxWebsocket({
    reconnectOnClose: false,
    reconnectOnError: false,
});

const summarizeSummaryView = (summaryView: any) => ({
    versionSummaryViewItemCount: summaryView?.versionSummaryViewItems?.length ?? 0,
    truncated: true,
});

const summarizeSummaryViewEntry = (entry: any) => ({
    scopeKey: entry?.scopeKey,
    rowSetSignature: entry?.rowSetSignature,
    fetchedPropertiesCount: entry?.fetchedProperties?.length ?? 0,
    rowCoverageCount: Object.keys(entry?.fetchedPropertiesByRowKey ?? {}).length,
    summaryView: summarizeSummaryView(entry?.summaryView),
    truncated: true,
});

const summarizeOrgDestinations = (data: any) => ({
    orgId: data?.orgId,
    appDestinationCount: data?.appDestinations?.length ?? 0,
    allDestinationIdsCount: data?.allDestinationIds?.length ?? 0,
    allVersionsCount: data?.allVersions?.length ?? 0,
    truncated: true,
});

const sanitizeReduxAction = (action: any) => {
    if (action?.type === FETCH_ORG_SUMMARY_VIEW_SUCCESS) {
        return {
            ...action,
            payload: {
                ...action.payload,
                rowSetSignature: action.payload?.rowSetSignature,
                fetchedPropertiesCount: action.payload?.fetchedProperties?.length ?? 0,
                summaryView: summarizeSummaryView(action.payload?.summaryView),
            },
        };
    }

    if (action?.type === FETCH_ORG_DESTINATIONS_AND_VERSIONS_SUCCESS) {
        return {
            ...action,
            payload: {
                ...action.payload,
                data: summarizeOrgDestinations(action.payload?.data),
            },
        };
    }

    return action;
};

const sanitizeSummaryViewState = (summaryViewState: any) => {
    const summarizedById: Record<string, unknown> = {};

    Object.keys(summaryViewState?.byId ?? {}).forEach((requestId) => {
        summarizedById[requestId] = summarizeSummaryViewEntry(summaryViewState.byId[requestId]);
    });

    const summarizedOrgDestinations: Record<string, unknown> = {};

    Object.keys(summaryViewState?.orgDestinations ?? {}).forEach((orgId) => {
        summarizedOrgDestinations[orgId] = summarizeOrgDestinations(summaryViewState.orgDestinations[orgId]);
    });

    return {
        ...summaryViewState,
        byId: summarizedById,
        orgDestinations: summarizedOrgDestinations,
    };
};

const sanitizeReduxState = (state: any) => {
    if (!state?.entities?.summaryView) {
        return state;
    }

    return {
        ...state,
        entities: {
            ...state.entities,
            summaryView: sanitizeSummaryViewState(state.entities.summaryView),
        },
    };
};

const logRocketReduxMiddleware = LogRocket.reduxMiddleware({
    actionSanitizer: sanitizeReduxAction,
    stateSanitizer: sanitizeReduxState,
});

// expose Logrocket to the window object (required for integration into userflow)
window.LogRocket = LogRocket;

// Create Redux Store
const demoPath = 'ui.uiConfig.demoData';
const store = configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            immutableCheck: {
                ignoredPaths: [demoPath],
            },
            serializableCheck: {
                ignoredPaths: [demoPath],
                ignoredActions: [
                    REDUX_WEBSOCKET_MESSAGE,
                    REDUX_WEBSOCKET_OPEN,
                    REDUX_WEBSOCKET_CLOSED,
                    REDUX_WEBSOCKET_ERROR,
                    REDUX_WEBSOCKET_CONNECT,
                ],
            },
        })
            .concat(sagaMiddleware)
            .concat(reduxWebsocketMiddleware)
            .concat(logRocketReduxMiddleware)
            .concat(defaultApi.middleware)
            .concat(paginationApi.middleware),
    devTools: {
        actionSanitizer: sanitizeReduxAction,
        stateSanitizer: sanitizeReduxState,
    },
});

sagaMiddleware.run(rootSaga);

const additionalGaParams = isDevEnvironment ? { debug_mode: true } : {};

// Update tracker paths upon history location changes

history.listen((location) => {
    // GA
    config(GA_MEASUREMENT_ID, {
        page_path: location.pathname,
        send_page_view: false,
        ...additionalGaParams,
    });

    // Hubspot Tracking
    HubspotUtils.updatePath(location.pathname);
});

Spin.setDefaultIndicator(<LoadingOutlined spin style={{ fontSize: 30, color: runwayColors.info }} />);

// data-color-mode is set dynamically in themeMode.tsx to match the active theme

// Userflow init is deferred until after org scripts config is loaded.
// See initializeClientScripts() in scriptManager.ts.

ReactDOM.render(
    <React.StrictMode>
        <Provider store={store}>
            <ThemeModeProvider>
                <Router history={history}>
                    <StyleProvider hashPriority="high">
                        <ThemedConfigProvider environment={environment} history={history} />
                    </StyleProvider>
                </Router>
            </ThemeModeProvider>
        </Provider>
    </React.StrictMode>,
    document.getElementById('main'),
);

function ThemedConfigProvider({
    environment,
    history,
}: {
    environment: Environment;
    history: ReturnType<typeof createBrowserHistory>;
}) {
    const { mode } = useThemeMode();
    const isDark = mode === 'dark';

    return (
        <ConfigProvider
            form={{ validateMessages: { required: "'${label}' is required" } }}
            modal={{
                styles: {
                    content: {
                        backgroundColor: antGrey.grey2,
                        padding: 0,
                    },
                    body: {
                        padding: 24,
                    },
                    header: {
                        padding: '16px 24px',
                        marginBottom: 0,
                    },
                    footer: {
                        padding: '16px 24px',
                    },
                },
            }}
            theme={{
                algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
                token: getAntdThemeTokens(isDark ? 'dark' : 'light'),
            }}
        >
            <Switch>
                <Route path={[routes.publicBucket]} component={PublicAppContainer} />
                <MainAppContainer environment={environment} history={history} />
            </Switch>
        </ConfigProvider>
    );
}
